'use client';
import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Meenakshi Sundaram',
    location: 'Chennai, Tamil Nadu',
    ritual: 'Gruhapravesam',
    rating: 5,
    text: 'Pandit Venkateshwara conducted our Gruhapravesam with such authenticity and devotion. Every mantra was explained in Tamil, making the ceremony deeply meaningful for our entire family. The booking was seamless — I found the right priest within minutes.',
    avatar: '👩',
    date: 'March 2026',
  },
  {
    id: 2,
    name: 'Rajesh Krishnaswamy',
    location: 'Bengaluru, Karnataka',
    ritual: 'Satyanarayan Pooja',
    rating: 5,
    text: 'I was skeptical about booking a priest online, but Yagnick completely changed my perspective. Pandit Sharma arrived on time, brought his own samagri, and performed the Satyanarayan Katha flawlessly. The AI suggestion for the muhurta was spot on.',
    avatar: '👨',
    date: 'February 2026',
  },
  {
    id: 3,
    name: 'Lakshmi Narasimhan',
    location: 'Hyderabad, Telangana',
    ritual: 'Vivaha Samskar',
    rating: 5,
    text: 'We used Yagnick for our daughter\'s wedding rituals. The sampradaya matching was incredible — Pandit Dikshitar followed every Shaiva tradition exactly as our family has for generations. Worth every rupee.',
    avatar: '👩',
    date: 'January 2026',
  },
  {
    id: 4,
    name: 'Suresh Patel',
    location: 'Mumbai, Maharashtra',
    ritual: 'Ganesh Pooja',
    rating: 5,
    text: 'Quick, reliable, and deeply spiritual. Pandit Bhatt was available within 2 hours for an urgent Ganesh Pooja before my business meeting. The AI correctly identified my need and matched me with a Smartha priest who speaks Gujarati. Excellent platform!',
    avatar: '👨',
    date: 'March 2026',
  },
];

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const prev = () => {
    setIsAutoPlaying(false);
    setCurrent((c) => (c - 1 + testimonials?.length) % testimonials?.length);
  };

  const next = () => {
    setIsAutoPlaying(false);
    setCurrent((c) => (c + 1) % testimonials?.length);
  };

  const t = testimonials?.[current];

  return (
    <section className="py-16 px-4 sm:px-6 bg-white">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-saffron mb-3">
            Devotee Stories
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-maroon mb-2">
            Sacred Experiences
          </h2>
          <p className="text-maroon/60 text-base font-body">
            Real stories from devotees across India
          </p>
          <div className="mt-3 w-16 h-0.5 gold-gradient mx-auto rounded-full" />
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="relative bg-gradient-to-br from-cream to-ivory rounded-3xl p-8 md:p-12 border border-gold/20 shadow-xl">
            {/* Quote Icon */}
            <div className="absolute top-6 left-8">
              <Quote size={40} className="text-gold/20" fill="rgba(212,175,55,0.15)" />
            </div>

            {/* Stars */}
            <div className="flex items-center gap-1 mb-6">
              {[1, 2, 3, 4, 5]?.map((s) => (
                <Star key={s} size={18} className="star-filled" fill="#D4AF37" stroke="#D4AF37" />
              ))}
            </div>

            {/* Text */}
            <p className="font-body text-maroon/80 text-lg leading-relaxed mb-8 relative z-10">
              "{t?.text}"
            </p>

            {/* Author */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-saffron/20 to-gold/20 border border-gold/30 flex items-center justify-center text-2xl">
                  {t?.avatar}
                </div>
                <div>
                  <div className="font-display font-semibold text-maroon">{t?.name}</div>
                  <div className="text-sm text-maroon/50 font-body">{t?.location}</div>
                </div>
              </div>
              <div className="text-right hidden sm:block">
                <div className="text-xs font-semibold text-saffron bg-saffron/10 px-3 py-1 rounded-full">
                  {t?.ritual}
                </div>
                <div className="text-xs text-maroon/40 mt-1">{t?.date}</div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gold/20">
              <button
                onClick={prev}
                className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-maroon hover:bg-gold/10 hover:border-gold transition-all"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="flex gap-2">
                {testimonials?.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setIsAutoPlaying(false); setCurrent(i); }}
                    className={`rounded-full transition-all duration-300 ${
                      i === current ? 'w-6 h-2 bg-saffron' : 'w-2 h-2 bg-gold/30 hover:bg-gold/60'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={next}
                className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-maroon hover:bg-gold/10 hover:border-gold transition-all"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}